import type { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Badge from '@cloudscape-design/components/badge';
import Spinner from '@cloudscape-design/components/spinner';
import { useTenant } from './TenantContext';
import type { TenantInfo } from '../api/client';

/** Centered full-height shell used by the loading / empty / error states. */
function Centered({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: '16px',
      }}
    >
      <img src="/mailhub-icon.svg" alt="MailHub" style={{ width: '64px', height: '64px' }} />
      <div style={{ fontSize: '18px', fontWeight: 500, color: '#0F1B2D' }}>MailHub</div>
      {children}
    </div>
  );
}

/**
 * WM-4: Tenant Picker — shown only when no mailbox has been selected yet
 * (i.e. the first sign-in). Once a mailbox is chosen it is persisted, and
 * subsequent visits go straight to that mailbox's inbox.
 */
export default function TenantPicker() {
  const { tenant, tenants, loading, selectTenant } = useTenant();
  const navigate = useNavigate();

  // Already chosen a mailbox — never show the picker again.
  if (tenant) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <Centered>
        <Spinner size="large" />
        <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading your mailboxes…</div>
      </Centered>
    );
  }

  if (tenants.length === 0) {
    return (
      <Centered>
        <Box color="text-body-secondary">
          No shared mailboxes available. Contact your admin to get onboarded.
        </Box>
      </Centered>
    );
  }

  function choose(t: TenantInfo) {
    selectTenant(t);
    navigate('/', { replace: true });
  }

  return (
    <div style={{ maxWidth: '900px', margin: '80px auto', padding: '0 24px' }}>
      <SpaceBetween size="l">
        <div style={{ textAlign: 'center' }}>
          <img
            src="/mailhub-icon.svg"
            alt="MailHub"
            style={{ width: '48px', height: '48px', marginBottom: '12px' }}
          />
          <Header variant="h1">Select a Mailbox</Header>
          <Box color="text-body-secondary">Choose a shared inbox to get started.</Box>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '16px',
          }}
        >
          {tenants.map((t) => (
            <button
              key={t.tenantId}
              type="button"
              onClick={() => choose(t)}
              aria-label={`Open ${t.displayName || t.tenantId}`}
              style={{
                textAlign: 'left',
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                background: '#ffffff',
                cursor: 'pointer',
                font: 'inherit',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#FF9900';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#0F1B2D' }}>
                {t.displayName || t.tenantId}
              </span>
              <span style={{ fontSize: '13px', color: '#6b7280', wordBreak: 'break-all' }}>
                {t.mailboxAddress || t.domain || '—'}
              </span>
              <Badge color="green">{t.status || 'ACTIVE'}</Badge>
            </button>
          ))}
        </div>
      </SpaceBetween>
    </div>
  );
}
